import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageCircle, PackageSearch, Phone, RefreshCcw, Send } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { metaApi } from '@/services/meta'
import { useAuthStore } from '@/stores/auth-store'
import { useTenantTimeZone } from '@/lib/tenant-date-time'

type UnknownRecord = Record<string, unknown>
type SentMessage = {
  to: string
  body: string
  sentAt: string
}

const asRecord = (value: unknown): UnknownRecord => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as UnknownRecord : {}
)

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

function extractWhatsAppMessageSummary(payload: unknown) {
  const root = asRecord(payload)
  for (const entry of asArray(root['entry'])) {
    const entryRecord = asRecord(entry)
    for (const change of asArray(entryRecord['changes'])) {
      const value = asRecord(asRecord(change)['value'])
      const contact = asRecord(asArray(value['contacts'])[0])
      const profile = asRecord(contact['profile'])
      const message = asRecord(asArray(value['messages'])[0])
      if (!Object.keys(message).length) {
        continue
      }

      const text = asRecord(message['text'])
      const order = asRecord(message['order'])
      const interactive = asRecord(message['interactive'])
      const buttonReply = asRecord(interactive['button_reply'])

      return {
        from: String(message['from'] || contact['wa_id'] || ''),
        name: String(profile['name'] || ''),
        type: String(message['type'] || 'message'),
        body: String(text['body'] || buttonReply['title'] || order['text'] || ''),
      }
    }
  }

  return null
}

const customerInitial = (nameOrPhone: string) => (
  nameOrPhone.trim().charAt(0).toUpperCase() || 'W'
)

const formatMessageTime = (value?: string | null, timeZone?: string | null) => {
  if (!value) {
    return ''
  }
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
}

export default function WhatsAppMessagesPage() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const authServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || authServiceInstanceId
  // Message times in the tenant timezone once it is known; until then the existing output.
  const messageTimeZone = useTenantTimeZone(serviceInstanceId)
  const [to, setTo] = useState('')
  const [body, setBody] = useState('')
  const [sentMessages, setSentMessages] = useState<SentMessage[]>([])

  const connectionQuery = useQuery({
    queryKey: ['meta-connection', serviceInstanceId],
    queryFn: () => metaApi.getConnection(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  const eventsQuery = useQuery({
    queryKey: ['meta-webhook-events', serviceInstanceId],
    queryFn: () => metaApi.listWebhookEvents(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      const message = {
        to: to.trim(),
        body: body.trim(),
        sentAt: new Date().toISOString(),
      }
      await metaApi.sendWhatsAppText(serviceInstanceId!, message)
      return message
    },
    onSuccess: async (message) => {
      toast.success('WhatsApp message sent')
      setSentMessages((current) => [message, ...current].slice(0, 10))
      setBody('')
      await queryClient.invalidateQueries({ queryKey: ['meta-webhook-events', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['meta-connection', serviceInstanceId] }),
      queryClient.invalidateQueries({ queryKey: ['meta-webhook-events', serviceInstanceId] }),
    ])
  }

  if (!serviceInstanceId) {
    return (
      <Card className="rounded-[24px]">
        <CardHeader>
          <CardTitle>WhatsApp Messages</CardTitle>
          <CardDescription>Select a branch before managing WhatsApp messages.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (connectionQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 rounded-[24px]" />
        <Skeleton className="h-72 rounded-[24px]" />
      </div>
    )
  }

  const connection = connectionQuery.data
  const configured = Boolean(connection?.has_access_token && connection?.phone_number_id)
  const events = eventsQuery.data ?? []
  const incomingMessages = events
    .slice(0, 25)
    .map((event) => ({
      event,
      summary: extractWhatsAppMessageSummary(event.raw_payload),
    }))
  const firstCustomer = incomingMessages.find((item) => item.summary?.from)?.summary
  const activeCustomerName = firstCustomer?.name || firstCustomer?.from || to || 'Customer'
  const activeCustomerPhone = to || firstCustomer?.from || 'Enter a phone number to start'

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">WhatsApp Messages</h1>
              <p className="text-sm text-muted-foreground">View customer messages and reply from this branch&apos;s WhatsApp number.</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to={withServiceInstanceId('/services/whatsapp/catalog', serviceInstanceId)}>
              <PackageSearch className="mr-2 h-4 w-4" />
              Catalog
            </Link>
          </Button>
          <Button variant="outline" onClick={refresh}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {!configured ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Configure Meta credentials for this branch before sending WhatsApp messages.
        </div>
      ) : null}

      <Card className="rounded-[24px] border-emerald-200 bg-emerald-50/60">
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <PackageSearch className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-foreground">WhatsApp Catalog</p>
              <p className="text-sm text-muted-foreground">View all packages currently added to WhatsApp.</p>
            </div>
          </div>
          <Button asChild>
            <Link to={withServiceInstanceId('/services/whatsapp/catalog', serviceInstanceId)}>
              Open Catalog
            </Link>
          </Button>
        </CardContent>
      </Card>

      <div className="grid min-h-[680px] gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <Card className="overflow-hidden rounded-[24px] border-border/80">
          <CardHeader className="border-b bg-muted/30">
            <CardTitle className="text-base">Inbox</CardTitle>
            <CardDescription>Recent customer messages</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 p-3">
            {incomingMessages.map(({ event, summary }) => {
              const name = summary?.name || summary?.from || 'Unknown customer'
              const preview = summary?.body || (summary?.type ? `${summary.type} message` : 'New WhatsApp message')
              return (
                <button
                  key={event.id}
                  type="button"
                  className="flex w-full items-start gap-3 rounded-2xl p-3 text-left transition hover:bg-muted"
                  onClick={() => {
                    if (summary?.from) {
                      setTo(summary.from)
                    }
                  }}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-semibold text-emerald-700">
                    {customerInitial(name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-3">
                      <span className="truncate font-medium text-foreground">{name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{formatMessageTime(event.received_at, messageTimeZone)}</span>
                    </span>
                    <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">{preview}</span>
                    {event.linked_order_id ? (
                      <span className="mt-2 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Order received
                      </span>
                    ) : null}
                  </span>
                </button>
              )
            })}
            {!incomingMessages.length ? (
              <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                No WhatsApp messages yet.
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="flex overflow-hidden rounded-[24px] border-border/80">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-semibold text-emerald-700">
                  {customerInitial(activeCustomerName)}
                </div>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{activeCustomerName}</p>
                  <p className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    <span className="truncate">{activeCustomerPhone}</span>
                  </p>
                </div>
              </div>
              <div className="rounded-full bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
                {connection?.status?.split('_').join(' ') || 'not connected'}
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto bg-[#efeae2] p-4">
              {incomingMessages.slice().reverse().map(({ event, summary }) => (
                <div key={event.id} className="flex justify-start">
                  <div className="max-w-[78%] rounded-2xl rounded-tl-sm bg-white px-4 py-3 text-sm shadow-sm">
                    <p className="whitespace-pre-wrap text-foreground">
                      {summary?.body || (summary?.type ? `${summary.type} message received` : 'WhatsApp message received')}
                    </p>
                    {event.linked_order_id ? (
                      <p className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
                        Order created from this message
                      </p>
                    ) : null}
                    <p className="mt-2 text-right text-xs text-muted-foreground">{formatMessageTime(event.received_at, messageTimeZone)}</p>
                  </div>
                </div>
              ))}

              {sentMessages.slice().reverse().map((message) => (
                <div key={`${message.sentAt}-${message.to}`} className="flex justify-end">
                  <div className="max-w-[78%] rounded-2xl rounded-tr-sm bg-[#d9fdd3] px-4 py-3 text-sm shadow-sm">
                    <p className="whitespace-pre-wrap text-foreground">{message.body}</p>
                    <p className="mt-2 text-right text-xs text-muted-foreground">{formatMessageTime(message.sentAt, messageTimeZone)}</p>
                  </div>
                </div>
              ))}

              {!incomingMessages.length && !sentMessages.length ? (
                <div className="flex h-full items-center justify-center">
                  <div className="rounded-2xl bg-white/80 px-5 py-4 text-center text-sm text-muted-foreground shadow-sm">
                    Start a WhatsApp conversation by entering a customer phone number below.
                  </div>
                </div>
              ) : null}
            </div>

            <div className="border-t bg-background p-4">
              <div className="mb-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                <div className="space-y-1">
                  <Label htmlFor="whatsapp-to">Customer phone</Label>
                  <Input id="whatsapp-to" value={to} onChange={(event) => setTo(event.target.value)} placeholder="447700900123" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="whatsapp-body">Message</Label>
                  <Textarea
                    id="whatsapp-body"
                    rows={3}
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    placeholder="Type a WhatsApp message..."
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  Sending from this branch&apos;s configured WhatsApp number.
                </p>
                <Button
                  disabled={!configured || sendMutation.isPending || !to.trim() || !body.trim()}
                  onClick={() => sendMutation.mutate()}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {sendMutation.isPending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
